import React, { useCallback, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { useGetSystemSettingsQuery, useUpdateSystemSettingMutation } from "../../services/nestjs-base-api/admin-api";
import {
  Breadcrumb,
  Button,
  Card,
  Form,
  InputNumber,
  Layout,
  message,
  Spin,
  Table,
  Typography,
  theme
} from "antd";
import { SettingOutlined } from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { UserRoleEnum } from "../../enums/UserRoleEnum";
import { ISystemSetting } from "../../services/nestjs-base-api/types";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

interface IEditingCell {
  id: number;
  value: string;
}

const AdminSystemSettingsPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [editingCell, setEditingCell] = useState<IEditingCell | null>(null);
  const [form] = Form.useForm();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Check if user is super admin
  const isSuperAdmin = user?.roles?.includes(UserRoleEnum.SuperAdmin);

  // Get system settings
  const {
    data: systemSettings,
    error: settingsError,
    isLoading: isLoadingSettings,
    refetch: refetchSettings
  } = useGetSystemSettingsQuery({
    jwtToken: user.jwtToken
  });

  // Update system setting mutation
  const [updateSystemSetting, { isLoading: isUpdating }] = useUpdateSystemSettingMutation();

  // Handle save setting
  const handleSaveSetting = useCallback(async (settingId: number) => {
    if (!editingCell || editingCell.id !== settingId) return;

    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const value = values[`setting_${settingId}`];

      await updateSystemSetting({
        jwtToken: user.jwtToken,
        id: settingId,
        payload: { value: value.toString() }
      }).unwrap();

      messageApi.success(intl.formatMessage({ id: 'systemSettings.updateSuccess' }) || 'Setting updated successfully');
      setEditingCell(null);
      refetchSettings();
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        logout();
      } else {
        messageApi.error(intl.formatMessage({ id: 'systemSettings.updateError' }) || 'Error updating setting');
      }
    }
  }, [editingCell, form, intl, logout, messageApi, refetchSettings, updateSystemSetting, user.jwtToken]);

  // Handle edit setting
  const handleEditSetting = useCallback((setting: ISystemSetting) => {
    setEditingCell({ id: setting.id, value: setting.value });
    form.setFieldsValue({
      [`setting_${setting.id}`]: parseInt(setting.value, 10)
    });
  }, [form]);

  // Handle cancel edit
  const handleCancelEdit = useCallback(() => {
    setEditingCell(null);
    form.resetFields();
  }, [form]);

  // Get display name for setting
  const getSettingDisplayName = useCallback((name: string) => {
    const nameMap: Record<string, string> = {
      'MaxEvents': intl.formatMessage({ id: 'systemSettings.maxEvents' }) || 'Maximum Events per Organisation',
      'MaxGalleriesPerEvent': intl.formatMessage({ id: 'systemSettings.maxGalleriesPerEvent' }) || 'Maximum Galleries per Event',
      'MaxPhotosPerGallery': intl.formatMessage({ id: 'systemSettings.maxPhotosPerGallery' }) || 'Maximum Photos per Gallery'
    };
    return nameMap[name] || name;
  }, [intl]);

  // Get description for setting
  const getSettingDescription = useCallback((name: string) => {
    const descMap: Record<string, string> = {
      'MaxEvents': intl.formatMessage({ id: 'systemSettings.maxEvents.description' }) || 'Controls the maximum number of events an organisation can create',
      'MaxGalleriesPerEvent': intl.formatMessage({ id: 'systemSettings.maxGalleriesPerEvent.description' }) || 'Controls the maximum number of galleries that can be created per event',
      'MaxPhotosPerGallery': intl.formatMessage({ id: 'systemSettings.maxPhotosPerGallery.description' }) || 'Controls the maximum number of photos that can be uploaded to a gallery'
    };
    return descMap[name] || '';
  }, [intl]);

  // Table columns
  const columns = [
    {
      title: intl.formatMessage({ id: 'systemSettings.settingName' }) || 'Setting',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (name: string) => (
        <div>
          <Text strong>{getSettingDisplayName(name)}</Text>
          {getSettingDescription(name) && (
            <div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {getSettingDescription(name)}
              </Text>
            </div>
          )}
        </div>
      )
    },
    {
      title: intl.formatMessage({ id: 'systemSettings.type' }) || 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '15%',
      render: (type: string) => (
        <Text code>{type}</Text>
      )
    },
    {
      title: intl.formatMessage({ id: 'systemSettings.value' }) || 'Value',
      dataIndex: 'value',
      key: 'value',
      width: '25%',
      render: (value: string, record: ISystemSetting) => {
        if (editingCell?.id === record.id) {
          return (
            <Form form={form}>
              <Form.Item
                name={`setting_${record.id}`}
                style={{ margin: 0 }}
                rules={[
                  { required: true, message: intl.formatMessage({ id: 'systemSettings.valueRequired' }) || 'Value is required' },
                  { type: 'number', min: 1, message: intl.formatMessage({ id: 'systemSettings.minValue' }) || 'Minimum value is 1' }
                ]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder={intl.formatMessage({ id: 'systemSettings.enterValue' }) || 'Enter value'}
                />
              </Form.Item>
            </Form>
          );
        }
        return <Text>{value}</Text>;
      }
    },
    {
      title: intl.formatMessage({ id: 'systemSettings.lastUpdated' }) || 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: '20%',
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: intl.formatMessage({ id: 'common.actions' }) || 'Actions',
      key: 'actions',
      width: '10%',
      render: (_: any, record: ISystemSetting) => {
        if (editingCell?.id === record.id) {
          return (
            <>
              <Button
                type="link"
                size="small"
                loading={isUpdating}
                onClick={() => handleSaveSetting(record.id)}
              >
                {intl.formatMessage({ id: 'common.save' }) || 'Save'}
              </Button>
              <Button
                type="link"
                size="small"
                onClick={handleCancelEdit}
                disabled={isUpdating}
              >
                {intl.formatMessage({ id: 'common.cancel' }) || 'Cancel'}
              </Button>
            </>
          );
        }
        return (
          <Button
            type="link"
            size="small"
            onClick={() => handleEditSetting(record)}
          >
            {intl.formatMessage({ id: 'common.edit' }) || 'Edit'}
          </Button>
        );
      }
    }
  ];

  // Check for errors
  React.useEffect(() => {
    if (settingsError) {
      if ('status' in settingsError && settingsError.status === 401) {
        logout();
      }
    }
  }, [settingsError, logout]);

  // Check if user has access
  React.useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
    }
  }, [isSuperAdmin, navigate]);

  // Loading state
  if (isLoadingSettings) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>
            {intl.formatMessage({ id: 'systemSettings.loading' }) || 'Loading system settings...'}
          </Paragraph>
        </div>
      </Content>
    );
  }

  return (
    <Content style={{ margin: '0 16px' }}>
      {contextHolder}
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>{intl.formatMessage({ id: 'common.platformName' })}</Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={() => navigate('/admin')}><FormattedMessage id="section.adminName" /></a>
        </Breadcrumb.Item>
        <Breadcrumb.Item><FormattedMessage id="section.systemSettingsName" /></Breadcrumb.Item>
      </Breadcrumb>

      <div
        style={{
          padding: 24,
          minHeight: '79vh',
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>
            <SettingOutlined style={{ marginRight: 12 }} />
            {intl.formatMessage({ id: 'systemSettings.title' }) || 'System Settings'}
          </Title>
          <Paragraph type="secondary">
            {intl.formatMessage({ id: 'systemSettings.description' }) || 'Manage global system settings and limits'}
          </Paragraph>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={systemSettings}
            rowKey="id"
            pagination={false}
          />
        </Card>

        <Card style={{ marginTop: 24 }}>
          <Title level={4}>
            {intl.formatMessage({ id: 'systemSettings.importantNotes' }) || 'Important Notes'}
          </Title>
          <ul>
            <li>
              <Text>
                {intl.formatMessage({ id: 'systemSettings.note1' }) || 'These settings apply globally to all organisations in the system'}
              </Text>
            </li>
            <li>
              <Text>
                {intl.formatMessage({ id: 'systemSettings.note2' }) || 'Changes take effect immediately after saving'}
              </Text>
            </li>
            <li>
              <Text>
                {intl.formatMessage({ id: 'systemSettings.note3' }) || 'Organisation-specific features can override these limits if enabled'}
              </Text>
            </li>
          </ul>
        </Card>
      </div>
    </Content>
  );
};

export default AdminSystemSettingsPage;